import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { fetchEquipmentDetailForManagement, updateEquipment, deleteEquipment } from '../../services/api';
import './EquipmentDetailPage.css';

function EquipmentDetailPage() {
    const navigate = useNavigate();
    const { equipmentId } = useParams();
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);

    // URL 쿼리 파라미터에서 edit 모드 확인
    useEffect(() => {
        const editMode = searchParams.get('edit');
        if (editMode === 'true') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [searchParams]);

    const [formData, setFormData] = useState({
        id: equipmentId,
        equipmentCode: '',
        equipmentName: '',
        model: '',
        line: '',
        lineName: '',
        factory: '',
        factoryName: '',
        factoryId: null,
        lineId: null,
        serialNumber: '',
        manufacturer: '',
        installDate: '',
        warrantyEndDate: '',
        status: 'IDLE',
        maxTemperature: null,
        maxPressure: null,
        ratedPower: null,
        targetCycleTime: null,
        cavityCount: null,
        lastInspectionDate: '',
        nextMaintenanceDate: '',
        description: '',
        equipmentType: ''
    });

    // API에서 설비 데이터 가져오기
    useEffect(() => {
        const loadEquipmentDetail = async () => {
            try {
                setLoading(true);
                const data = await fetchEquipmentDetailForManagement(equipmentId);
                

                const transformedData = {
                    id: data.id,
                    equipmentCode: data.equipmentCode || '',
                    equipmentName: data.equipmentName || '',
                    model: data.model || '',
                    lineName: data.lineName || '-',
                    factoryName: data.factoryName || '-',
                    factoryId: data.factoryId,
                    lineId: data.lineId,
                    serialNumber: data.serialNumber || '-',
                    manufacturer: data.manufacturer || '-',
                    installDate: data.installDate || '',
                    warrantyEndDate: data.warrantyEndDate || '',
                    status: data.status || 'IDLE',
                    maxTemperature: data.maxTemperature || null,
                    maxPressure: data.maxPressure || null,
                    ratedPower: data.ratedPower || null,
                    targetCycleTime: data.targetCycleTime || null,
                    cavityCount: data.cavityCount || null,
                    lastInspectionDate: data.lastInspectionDate || '',
                    nextMaintenanceDate: data.nextMaintenanceDate || '',
                    description: data.description || '',
                    equipmentType: data.equipmentType || ''
                };

                setFormData(transformedData);
                setOriginalData(transformedData);
            } catch (err) {
                console.error('설비 상세 정보 조회 실패:', err);
                alert('설비 정보를 불러오는데 실패했습니다.');
            } finally {
                setLoading(false);
            }
        };

        if (equipmentId) {
            loadEquipmentDetail();
        }
    }, [equipmentId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = () => {
        // URL에 ?edit=true 쿼리 파라미터 추가
        navigate(`/equipment/${equipmentId}?edit=true`, { replace: true });
    };

    const handleCancel = () => {
        if (isEditing) {
            // 원래 데이터로 복원
            setFormData(originalData);
            // URL에서 ?edit=true 제거
            navigate(`/equipment/${equipmentId}`, { replace: true });
        } else {
            navigate('/equipment-list');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            // API 요청 데이터 구성
            const updateData = {
                factoryId: formData.factoryId,
                lineId: formData.lineId,
                equipmentCode: formData.equipmentCode,
                equipmentName: formData.equipmentName,
                model: formData.model,
                manufacturer: formData.manufacturer,
                serialNumber: formData.serialNumber,
                equipmentType: formData.equipmentType,
                installDate: formData.installDate || null,
                warrantyEndDate: formData.warrantyEndDate || null,
                maxTemperature: formData.maxTemperature,
                maxPressure: formData.maxPressure,
                ratedPower: formData.ratedPower,
                targetCycleTime: formData.targetCycleTime,
                cavityCount: formData.cavityCount,
                status: formData.status,
                productName: null,
                currentMoldId: null,
                specifications: null,
                lastInspectionDate: formData.lastInspectionDate || null,
                nextMaintenanceDate: formData.nextMaintenanceDate || null,
                description: formData.description
            };

            
            const result = await updateEquipment(equipmentId, updateData);

            alert('설비 정보가 성공적으로 수정되었습니다.');

            // 저장 후 데이터 다시 로드
            const updatedData = await fetchEquipmentDetailForManagement(equipmentId);
            const transformedData = {
                id: updatedData.id,
                equipmentCode: updatedData.equipmentCode || '',
                equipmentName: updatedData.equipmentName || '',
                model: updatedData.model || '',
                lineName: updatedData.lineName || '-',
                factoryName: updatedData.factoryName || '-',
                factoryId: updatedData.factoryId,
                lineId: updatedData.lineId,
                serialNumber: updatedData.serialNumber || '-',
                manufacturer: updatedData.manufacturer || '-',
                installDate: updatedData.installDate || '',
                warrantyEndDate: updatedData.warrantyEndDate || '',
                status: updatedData.status || 'IDLE',
                maxTemperature: updatedData.maxTemperature || null,
                maxPressure: updatedData.maxPressure || null,
                ratedPower: updatedData.ratedPower || null,
                targetCycleTime: updatedData.targetCycleTime || null,
                cavityCount: updatedData.cavityCount || null,
                lastInspectionDate: updatedData.lastInspectionDate || '',
                nextMaintenanceDate: updatedData.nextMaintenanceDate || '',
                description: updatedData.description || '',
                equipmentType: updatedData.equipmentType || ''
            };

            setFormData(transformedData);
            setOriginalData(transformedData);

            // 읽기 모드로 전환 (URL도 변경)
            navigate(`/equipment/${equipmentId}`, { replace: true });
        } catch (error) {
            console.error('설비 정보 업데이트 실패:', error);
            alert('설비 정보 수정에 실패했습니다.');
        }
    };

    const handleDelete = async () => {
        if (window.confirm('정말로 이 설비를 삭제하시겠습니까?')) {
            try {
                await deleteEquipment(equipmentId);
                alert('설비가 성공적으로 삭제되었습니다.');
                navigate('/equipment-list');
            } catch (error) {
                console.error('설비 삭제 실패:', error);
                alert('설비 삭제에 실패했습니다.');
            }
        }
    };

    // 상태 매핑 함수 (API -> 화면)
    const mapStatusToKorean = (apiStatus) => {
        const statusMap = {
            'RUNNING': '가동',
            'IDLE': '대기',
            'STOPPED': '정지',
            'MAINTENANCE': '점검',
            'FAULT': '고장',
            'OFFLINE': '오프라인'
        };
        return statusMap[apiStatus] || '대기';
    };

    // 상태 매핑 함수 (화면 -> API)
    const mapStatusToAPI = (koreanStatus) => {
        const statusMap = {
            '가동': 'RUNNING',
            '대기': 'IDLE',
            '정지': 'STOPPED',
            '점검': 'MAINTENANCE',
            '고장': 'FAULT',
            '오프라인': 'OFFLINE'
        };
        return statusMap[koreanStatus] || 'IDLE';
    };

    const getStatusClass = (status) => {
        const koreanStatus = typeof status === 'string' && status.length <= 4 ? status : mapStatusToKorean(status);
        switch (koreanStatus) {
            case '가동':
                return 'eq-status-running';
            case '대기':
                return 'eq-status-standby';
            case '점검':
                return 'eq-status-maintenance';
            case '고장':
                return 'eq-status-error';
            default:
                return 'eq-status-standby';
        }
    };

    const getEfficiencyClass = (efficiency) => {
        if (efficiency >= 90) return 'eq-efficiency-high';
        if (efficiency >= 70) return 'eq-efficiency-medium';
        if (efficiency > 0) return 'eq-efficiency-low';
        return 'eq-efficiency-none';
    };

    if (loading) {
        return (
            <MainLayout>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                    데이터를 불러오는 중...
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="equipment-detail-container">
                {/* Header Section */}
                <div className="equipment-detail-header">
                    <div className="equipment-detail-header-left">
                        <div className="eq-back-btn" onClick={() => navigate('/equipment-list')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <div className="equipment-detail-title">설비 상세 정보</div>
                            <p className="equipment-detail-subtitle">설비 정보 조회 및 수정</p>
                        </div>
                    </div>
                    <div className="equipment-detail-header-right">
                        {!isEditing ? (
                            <>
                                <button className="eq-delete-header-btn" onClick={handleDelete}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2 4H3.33333H14M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    삭제
                                </button>
                                <button className="eq-edit-header-btn" onClick={handleEdit}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.3333 2C11.5084 1.82493 11.7163 1.68605 11.9457 1.59135C12.1752 1.49665 12.4218 1.44803 12.6707 1.44803C12.9196 1.44803 13.1662 1.49665 13.3956 1.59135C13.6251 1.68605 13.833 1.82493 14.008 2C14.183 2.17507 14.3219 2.38299 14.4166 2.61245C14.5113 2.84191 14.5599 3.08852 14.5599 3.33744C14.5599 3.58636 14.5113 3.83297 14.4166 4.06243C14.3219 4.29189 14.183 4.49981 14.008 4.67488L5.33333 13.3495L1.33333 14.683L2.66667 10.6829L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    수정
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="eq-cancel-header-btn" onClick={handleCancel}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    취소
                                </button>
                                <button className="eq-save-header-btn" onClick={handleSave}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    저장
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content Section */}
                <div className="equipment-detail-content">
                    {/* Equipment Profile Card */}
                    <div className="eq-profile-card">
                        <div className="eq-profile-icon-large">
                            <span className="material-symbols-outlined">precision_manufacturing</span>
                        </div>
                        <div className="eq-profile-info">
                            <h2 className="eq-profile-name">{formData.equipmentName}</h2>
                            <p className="eq-profile-model">{formData.model}</p>
                            <span className={`eq-status-badge-large ${getStatusClass(formData.status)}`}>
                                {mapStatusToKorean(formData.status)}
                            </span>
                        </div>
                        <div className="eq-profile-stats">
                            <div className="eq-stat-item">
                                <span className="eq-stat-label">캐비티</span>
                                <span className="eq-stat-value">{formData.cavityCount || '-'}</span>
                            </div>
                            <div className="eq-stat-item">
                                <span className="eq-stat-label">목표 사이클</span>
                                <span className="eq-stat-value">
                                    {formData.targetCycleTime ? `${(formData.targetCycleTime / 1000).toFixed(1)}s` : '-'}
                                </span>
                            </div>
                        </div>
                        <div className="eq-profile-meta">
                            <div className="eq-meta-item">
                                <span className="eq-meta-label">설치일</span>
                                <span className="eq-meta-value">{formData.installDate || '-'}</span>
                            </div>
                            <div className="eq-meta-item">
                                <span className="eq-meta-label">최종점검일</span>
                                <span className="eq-meta-value">{formData.lastInspectionDate || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Detail Form */}
                    <form onSubmit={handleSave}>
                        {/* 기본 정보 섹션 */}
                        <div className="eq-detail-section">
                            <h3 className="eq-detail-section-title">기본 정보</h3>
                            <div className="eq-detail-grid">
                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">설비명</label>
                                    {isEditing ? (
                                        <input type="text" name="equipmentName" className="eq-detail-input" value={formData.equipmentName} onChange={handleInputChange} required />
                                    ) : (
                                        <div className="eq-detail-value">{formData.equipmentName}</div>
                                    )}
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">설비 코드</label>
                                    <div className="eq-detail-value">{formData.equipmentCode}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">모델</label>
                                    {isEditing ? (
                                        <input type="text" name="model" className="eq-detail-input" value={formData.model} onChange={handleInputChange} required />
                                    ) : (
                                        <div className="eq-detail-value">{formData.model}</div>
                                    )}
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">소속 공장</label>
                                    <div className="eq-detail-value">{formData.factoryName}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">라인</label>
                                    <div className="eq-detail-value">{formData.lineName}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">제조사</label>
                                    <div className="eq-detail-value">{formData.manufacturer}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">시리얼넘버</label>
                                    <div className="eq-detail-value">{formData.serialNumber}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">설치일</label>
                                    <div className="eq-detail-value">{formData.installDate || '-'}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">보증 종료일</label>
                                    <div className="eq-detail-value">{formData.warrantyEndDate || '-'}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">상태</label>
                                    {isEditing ? (
                                        <div className="eq-detail-dropdown-container">
                                            <select name="status" className="eq-detail-select" value={formData.status} onChange={handleInputChange} required>
                                                <option value="RUNNING">가동</option>
                                                <option value="IDLE">대기</option>
                                                <option value="STOPPED">정지</option>
                                                <option value="MAINTENANCE">점검</option>
                                                <option value="FAULT">고장</option>
                                                <option value="OFFLINE">오프라인</option>
                                            </select>
                                            <svg className="eq-detail-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="eq-detail-value">{mapStatusToKorean(formData.status)}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 운영 현황 섹션 */}
                        <div className="eq-detail-section">
                            <h3 className="eq-detail-section-title">사양 정보</h3>
                            <div className="eq-detail-grid">
                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">최대 온도</label>
                                    <div className="eq-detail-value eq-temperature">
                                        {formData.maxTemperature ? `${formData.maxTemperature}°C` : '-'}
                                    </div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">최대 압력</label>
                                    <div className="eq-detail-value eq-pressure">
                                        {formData.maxPressure ? `${formData.maxPressure} MPa` : '-'}
                                    </div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">정격 전력</label>
                                    <div className="eq-detail-value">
                                        {formData.ratedPower ? `${formData.ratedPower} kW` : '-'}
                                    </div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">목표 사이클 타임</label>
                                    <div className="eq-detail-value eq-cycle-time">
                                        {formData.targetCycleTime ? `${(formData.targetCycleTime / 1000).toFixed(1)}s` : '-'}
                                    </div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">캐비티 수</label>
                                    <div className="eq-detail-value">{formData.cavityCount || '-'}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">마지막 점검일</label>
                                    <div className="eq-detail-value">{formData.lastInspectionDate || '-'}</div>
                                </div>

                                <div className="eq-detail-group">
                                    <label className="eq-detail-label">다음 정비일</label>
                                    <div className="eq-detail-value">{formData.nextMaintenanceDate || '-'}</div>
                                </div>

                                <div className="eq-detail-group eq-detail-group-full">
                                    <label className="eq-detail-label">비고</label>
                                    {isEditing ? (
                                        <textarea name="description" className="eq-detail-textarea" value={formData.description || ''} onChange={handleInputChange} rows="4" />
                                    ) : (
                                        <div className="eq-detail-value">{formData.description || '-'}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        {isEditing && (
                            <div className="eq-detail-form-actions">
                                <button type="button" className="eq-detail-btn-secondary" onClick={handleCancel}>취소</button>
                                <button type="submit" className="eq-detail-btn-primary">저장</button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </MainLayout>
    );
}

export default EquipmentDetailPage;
